import React, { useState, useEffect, useCallback } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import Login from './components/Login';
import Header from './components/Header';
import Sidebar from './components/Sidebar';
import Dashboard from './components/Dashboard';
import Biler from './components/Biler';
import Stationer from './components/Stationer';
import Fejlmeldinger from './components/Fejlmeldinger';
import Services from './components/Services';
import Vaerksted from './components/Vaerksted';
import VaerkstedDetails from './components/VaerkstedDetails';
// import VaerkstedDetails from './components/VaerkstedDetails'; // planned 1:1 skema visning
import Laanebiler from './components/Laanebiler';
import Salg from './components/Salg';
import Eftersyn from './components/Eftersyn';
import Samarbejdspartnere from './components/Samarbejdspartnere';
import BilProfil from './components/BilProfil';
import BilEdit from './components/BilEdit';
import StationProfil from './components/StationProfil';
import StationEdit from './components/StationEdit';
import ServiceSelection from './components/ServiceSelection';
import ServiceDetails from './components/ServiceDetails';
import Indstillinger from './components/Indstillinger';
import MinProfil from './components/MinProfil';
import PrintCalculator from './components/PrintCalculator';
import Afspadsering from './components/Afspadsering';
import Kiosk from './components/Kiosk';
import SettingsB2BPartnerForm from './pages/SettingsB2BPartnerForm';
import InventoryItems from './pages/InventoryItems';
import InventoryItemForm from './pages/InventoryItemForm';
import InventoryPickTablet from './pages/InventoryPickTablet';
import OrdersList from './pages/OrdersList';
import OrderNew from './pages/OrderNew';
import OrderView from './pages/OrderView';
import OrderReceive from './pages/OrderReceive';
import './App.css';
import { DEFAULT_ROLE_ACCESS } from './constants/accessControl';
import { applySnowPreference, applyThemePreference, getStoredSnowPreference, getStoredThemePreference } from './utils/theme';

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authLoading, setAuthLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [allowedPages, setAllowedPages] = useState(null);
  const [permissionsLoading, setPermissionsLoading] = useState(true);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false); // mobile overlay
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [pendingLoginReload, setPendingLoginReload] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(() => {
    try {
      const persisted = localStorage.getItem('sidebarCollapsed');
      return persisted === 'true';
    } catch (_) {
      return false;
    }
  });
  const isKioskRoute = typeof window !== 'undefined' && window.location.pathname.startsWith('/kiosk');

  const fetchAccessPermissions = useCallback(async (token, role, options = {}) => {
    const { silent = false } = options;
    const storageKey = (roleValue) => `allowedPages_${roleValue || 'ukendt'}`;
    if (!token) {
      setAllowedPages(new Set(DEFAULT_ROLE_ACCESS.default));
      if (!silent) {
        setPermissionsLoading(false);
      }
      return;
    }
    if (!silent) {
      setPermissionsLoading(true);
    }
    try {
      const response = await fetch('/api/indstillinger/access-control/allowed', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (!response.ok) {
        throw new Error('Kunne ikke hente adgang');
      }
      const data = await response.json();
      const allowed = Array.isArray(data.allowed) ? data.allowed : [];
      const effectiveRole = data.role || role;
      setAllowedPages(new Set(allowed));
      try { localStorage.setItem(storageKey(effectiveRole), JSON.stringify(allowed)); } catch (_) {}
    } catch (error) {
      const cached = (() => {
        try { return JSON.parse(localStorage.getItem(storageKey(role)) || 'null'); }
        catch (_) { return null; }
      })();
      if (Array.isArray(cached)) {
        setAllowedPages(new Set(cached));
      } else {
        const fallbackList = role && DEFAULT_ROLE_ACCESS[role] ? DEFAULT_ROLE_ACCESS[role] : DEFAULT_ROLE_ACCESS.default;
        setAllowedPages(new Set(fallbackList));
      }
    } finally {
      if (!silent) {
        setPermissionsLoading(false);
      }
    }
  }, []);

  // Initialiser dark mode også på login-siden (før Sidebar er mountet)
  useEffect(() => {
    applySnowPreference(getStoredSnowPreference());
    const applyTheme = (themeHours) => {
      const savedTheme = getStoredThemePreference();
      applyThemePreference(savedTheme, themeHours);
    };

    try {
      // Først brug evt. gemte timer
      const stored = (() => { try { return JSON.parse(localStorage.getItem('themeHours') || 'null'); } catch (_) { return null; } })();
      applyTheme(stored);

      // Hent friske timer fra backend
      fetch('/api/indstillinger/theme')
        .then(r => r.ok ? r.json() : null)
        .then(data => {
          if (data && typeof data.auto_start_hour === 'number' && typeof data.auto_end_hour === 'number') {
            const hoursOnly = {
              auto_start_hour: data.auto_start_hour,
              auto_end_hour: data.auto_end_hour
            };
            try { localStorage.setItem('themeHours', JSON.stringify(hoursOnly)); } catch (_) {}
            applyTheme(hoursOnly);
          }

          if (data && typeof data.snow_enabled === 'boolean') {
            applySnowPreference(data.snow_enabled);
          }
        })
        .catch(() => {});

      // Opdater tema periodisk så det skifter automatisk når tiden passerer grænsen
      const intervalId = setInterval(() => {
        const stored = (() => { try { return JSON.parse(localStorage.getItem('themeHours') || 'null'); } catch (_) { return null; } })();
        applyTheme(stored);
      }, 60 * 1000); // hvert minut

      return () => clearInterval(intervalId);
    } catch (_) {}
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      fetch('/api/auth/verify', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })
        .then(response => response.json())
        .then(data => {
          if (data.valid) {
            setIsAuthenticated(true);
            setUser(data.user);
            try { localStorage.setItem('userRole', data.user.role || 'bruger'); } catch (_) {}
            fetchAccessPermissions(token, data.user.role);
          } else {
            localStorage.removeItem('token');
            try { localStorage.removeItem('userRole'); } catch (_) {}
            fetchAccessPermissions(null, null);
          }
        })
        .catch(() => {
          localStorage.removeItem('token');
          try { localStorage.removeItem('userRole'); } catch (_) {}
          fetchAccessPermissions(null, null);
        })
        .finally(() => setAuthLoading(false));
    } else {
      fetchAccessPermissions(null, null);
      setAuthLoading(false);
    }
  }, [fetchAccessPermissions]);

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(Boolean(document.fullscreenElement));
    };
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    handleFullscreenChange();
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  useEffect(() => {
    if (!pendingLoginReload || typeof window === 'undefined') return;
    const timeoutId = setTimeout(() => {
      try {
        window.location.replace('/');
      } catch (_) {
        window.location.reload();
      }
    }, 50);
    return () => clearTimeout(timeoutId);
  }, [pendingLoginReload]);

  const handleLogin = (userData, token) => {
    const hadToken = (() => {
      try { return Boolean(localStorage.getItem('token')); } catch (_) { return false; }
    })();
    setIsAuthenticated(true);
    setUser(userData);
    localStorage.setItem('token', token);
    try { localStorage.setItem('userRole', userData?.role || 'bruger'); } catch (_) {}
    const fallbackRole = userData?.role || 'bruger';
    const fallbackList = DEFAULT_ROLE_ACCESS[fallbackRole] || DEFAULT_ROLE_ACCESS.default;
    setAllowedPages(new Set(fallbackList));
    setPermissionsLoading(false);
    fetchAccessPermissions(token, fallbackRole, { silent: true });
    if (!hadToken) {
      setPendingLoginReload(true);
    }
  };

  const handleLogout = () => {
    if (user?.role) {
      try { localStorage.removeItem(`allowedPages_${user.role}`); } catch (_) {}
    }
    setIsAuthenticated(false);
    setUser(null);
    localStorage.removeItem('token');
    try { localStorage.removeItem('userRole'); } catch (_) {}
    setAllowedPages(null);
    setPermissionsLoading(false);
  };

  const hasAccess = useCallback((pageId) => {
    if (!pageId) return true;
    if (allowedPages instanceof Set) {
      return allowedPages.has(pageId);
    }
    if (Array.isArray(allowedPages)) {
      return allowedPages.includes(pageId);
    }
    return false;
  }, [allowedPages]);

  const renderWithAccess = useCallback((pageId, element, options = {}) => {
    const { fallback = '/', fallbackPermission, requireAdmin = false } = options;
    const accessDeniedElement = (
      <div className="p-6 text-center text-red-600 dark:text-red-400">
        Du har ikke adgang til denne side.
      </div>
    );
    if (requireAdmin && user?.role !== 'admin') {
      if (fallback && (!fallbackPermission || hasAccess(fallbackPermission))) {
        return <Navigate to={fallback} replace />;
      }
      return accessDeniedElement;
    }
    if (hasAccess(pageId)) {
      return element;
    }
    if (fallback && (!fallbackPermission || hasAccess(fallbackPermission))) {
      return <Navigate to={fallback} replace />;
    }
    return accessDeniedElement;
  }, [hasAccess, user?.role]);

  if (isKioskRoute) {
    return <Kiosk />;
  }

  if (pendingLoginReload) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-falck-blue"></div>
      </div>
    );
  }

  if (authLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-falck-blue"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Login onLogin={handleLogin} />;
  }

  // Første login: kræv kodeordsændring
  if (user && user.first_login) {
    return <MinProfil />;
  }

  if (permissionsLoading || !(allowedPages instanceof Set)) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-falck-blue"></div>
      </div>
    );
  }

  return (
    <Router>
      <div className={`flex h-screen ${isFullscreen ? 'h-[100dvh]' : ''} bg-gray-100 dark:bg-gray-900`}>
        {!isFullscreen && (
          <Sidebar
            isOpen={isSidebarOpen}
            onClose={() => setIsSidebarOpen(false)}
            isCollapsed={isSidebarCollapsed}
            onToggleCollapse={() => {
              setIsSidebarCollapsed(prev => {
                const next = !prev;
                try { localStorage.setItem('sidebarCollapsed', String(next)); } catch (_) {}
                return next;
              });
            }}
            allowedPages={allowedPages}
            userRole={user?.role}
          />
        )}
          <div className="flex-1 flex flex-col overflow-hidden">
            {!isFullscreen && (
              <Header
                user={user}
                onLogout={handleLogout}
                onOpenSidebar={() => setIsSidebarOpen(true)}
                isSidebarCollapsed={isSidebarCollapsed}
                onToggleSidebarCollapse={() => {
                  setIsSidebarCollapsed(prev => {
                    const next = !prev;
                    try { localStorage.setItem('sidebarCollapsed', String(next)); } catch (_) {}
                    return next;
                  });
                }}
              />
            )}
            <main className="flex-1 overflow-x-hidden overflow-y-auto bg-gray-100 dark:bg-gray-900 px-2 sm:px-4 md:px-6 py-2 sm:py-4 text-gray-900 dark:text-gray-100 min-h-0">
              <Routes>
                <Route path="/" element={renderWithAccess('dashboard', <Dashboard />, { fallback: '/min-profil', fallbackPermission: 'min_profil' })} />
                <Route path="/biler" element={renderWithAccess('biler', <Biler />, { fallbackPermission: 'dashboard' })} />
                <Route path="/biler/profil/:id" element={renderWithAccess('biler', <BilProfil />, { fallbackPermission: 'dashboard' })} />
                <Route path="/biler/edit/:id" element={renderWithAccess('biler_edit', <BilEdit />, { fallbackPermission: 'dashboard' })} />
                <Route path="/stationer" element={renderWithAccess('stationer', <Stationer />, { fallbackPermission: 'dashboard' })} />
                <Route path="/stationer/profil/:id" element={renderWithAccess('stationer', <StationProfil />, { fallbackPermission: 'dashboard' })} />
                <Route path="/stationer/edit/:id" element={renderWithAccess('stationer_edit', <StationEdit />, { fallbackPermission: 'dashboard' })} />
                <Route path="/afspadsering" element={renderWithAccess('afspadsering', <Afspadsering />, { fallbackPermission: 'dashboard' })} />
                <Route path="/fejlmeldinger" element={renderWithAccess('fejlmeldinger', <Fejlmeldinger />, { fallbackPermission: 'dashboard' })} />
                <Route path="/services" element={renderWithAccess('services', <Services />, { fallbackPermission: 'dashboard' })} />
                <Route path="/services/selection" element={renderWithAccess('services_selection', <ServiceSelection />, { fallbackPermission: 'dashboard' })} />
                <Route path="/services/details" element={renderWithAccess('services_details', <ServiceDetails />, { fallbackPermission: 'dashboard' })} />
                <Route path="/vaerksted" element={renderWithAccess('vaerksted', <Vaerksted />, { fallbackPermission: 'dashboard' })} />
                <Route path="/vaerksted/details" element={renderWithAccess('vaerksted_details', <VaerkstedDetails />, { fallbackPermission: 'dashboard' })} />
                <Route path="/laanebiler" element={renderWithAccess('laanebiler', <Laanebiler />, { fallbackPermission: 'dashboard' })} />
                <Route path="/salg" element={renderWithAccess('salg', <Salg />, { fallbackPermission: 'dashboard' })} />
                <Route path="/eftersyn" element={renderWithAccess('eftersyn', <Eftersyn />, { fallbackPermission: 'dashboard' })} />
                <Route path="/samarbejdspartnere" element={renderWithAccess('samarbejdspartnere', <Samarbejdspartnere />, { fallbackPermission: 'dashboard' })} />
                <Route path="/3d-priser" element={renderWithAccess('print_calculator', <PrintCalculator />, { fallbackPermission: 'dashboard' })} />
                <Route path="/indstillinger/b2b" element={renderWithAccess('b2b_partners', <Indstillinger initialTab="b2b" />, { requireAdmin: true, fallbackPermission: 'dashboard' })} />
                <Route path="/indstillinger/b2b/ny" element={renderWithAccess('b2b_partners', <SettingsB2BPartnerForm />, { fallbackPermission: 'dashboard' })} />
                <Route path="/indstillinger/b2b/:id" element={renderWithAccess('b2b_partners', <SettingsB2BPartnerForm />, { fallbackPermission: 'dashboard' })} />
                <Route path="/lager/varer" element={renderWithAccess('inventory_items', <InventoryItems />, { fallbackPermission: 'dashboard' })} />
                <Route path="/lager/varer/ny" element={renderWithAccess('inventory_items', <InventoryItemForm />, { fallbackPermission: 'dashboard' })} />
                <Route path="/lager/varer/:id" element={renderWithAccess('inventory_items', <InventoryItemForm />, { fallbackPermission: 'dashboard' })} />
                <Route path="/lager/pluk" element={renderWithAccess('inventory_pick', <InventoryPickTablet />, { fallbackPermission: 'dashboard' })} />
                <Route path="/indkoeb/ordrer" element={renderWithAccess('orders', <OrdersList />, { fallbackPermission: 'dashboard' })} />
                <Route path="/indkoeb/ordrer/ny" element={renderWithAccess('orders', <OrderNew />, { fallbackPermission: 'dashboard' })} />
                <Route path="/indkoeb/ordrer/:id" element={renderWithAccess('orders', <OrderView />, { fallbackPermission: 'dashboard' })} />
                <Route path="/indkoeb/ordrer/:id/modtag" element={renderWithAccess('orders', <OrderReceive />, { fallbackPermission: 'dashboard' })} />
                <Route path="/indstillinger" element={renderWithAccess('indstillinger', <Indstillinger />, { requireAdmin: true, fallbackPermission: 'dashboard' })} />
                <Route path="/min-profil" element={renderWithAccess('min_profil', <MinProfil />, { fallbackPermission: 'dashboard' })} />
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </main>
          </div>
      </div>
    </Router>
  );
}

export default App;
